// Shared async job-status poller with the ResearchPage semantics:
// fixed interval, decaying cumulative error counter (hard cap), immediate
// surfacing of backend job-level errors (including "expired"), clean timeout.
//
// Usage:
//   await pollJob({
//     fetchStatus: () => api.status(jobId).then(r => r.data),
//     isComplete: (d) => d.status === 'complete',
//     onProgress: (d) => setProgress(d),
//     onComplete: (d) => setResult(d),
//     onError: (msg) => toast.error(msg),
//   });
export async function pollJob({
  fetchStatus,
  isComplete = (d) => d && (d.status === 'complete' || d.status === 'review'),
  isError = (d) => d && (d.status === 'error' || d.status === 'expired'),
  getError = (d) => (d && d.error) || 'The job failed. Please try again.',
  onProgress = () => {},
  onComplete = () => {},
  onError = () => {},
  intervalMs = 2500,
  maxPolls = 240,
  errorCap = 8,
} = {}) {
  let pollErrors = 0;
  for (let i = 0; i < maxPolls; i++) {
    await new Promise((r) => setTimeout(r, intervalMs));
    let data;
    try {
      data = await fetchStatus();
      if (pollErrors > 0) pollErrors -= 1; // cumulative count, decays on success
    } catch (err) {
      pollErrors += 1;
      if (pollErrors >= errorCap) {
        onError('Lost connection to the server while checking progress. Please try again.');
        return;
      }
      continue;
    }
    if (isError(data)) {
      onError(getError(data));
      return;
    }
    onProgress(data);
    if (isComplete(data)) {
      onComplete(data);
      return;
    }
  }
  onError('This is taking longer than expected. Please refresh and try again.');
}
